import {
  INSIGHTS_CHART_TYPES,
  InsightsChartSpec,
  InsightsChartType,
} from 'services/streamInsightsQuery';

// Human labels for the inline chart-type selector. Keyed to the same set as
// INSIGHTS_CHART_TYPES so the selector always has a label for whatever the agent
// produced.
export const CHART_TYPE_LABELS: Record<InsightsChartType, string> = {
  bar: 'Bar',
  column: 'Column',
  line: 'Line',
  area: 'Area',
  pie: 'Pie',
  donut: 'Donut',
  radar: 'Radar',
  waterfall: 'Waterfall',
  packedBubble: 'Packed bubble',
  funnel: 'Funnel',
  pyramid: 'Pyramid',
  scatter: 'Scatter',
  bubble: 'Bubble',
  heatmap: 'Heatmap',
  sankey: 'Sankey',
};

// One-line explainers shown in the chart-type selector's preview panel, telling
// the user what each type is good for. Keyed to the same set as
// INSIGHTS_CHART_TYPES so the selector always has copy for whatever is offered.
export const CHART_TYPE_DESCRIPTIONS: Record<InsightsChartType, string> = {
  bar: 'Compare a value across categories, ranked horizontally.',
  column: 'Compare a value across categories as vertical bars.',
  line: 'Follow how a value changes over time.',
  area: 'Follow a trend over time with the volume beneath it filled in.',
  pie: 'Show how a single total splits into parts.',
  donut: 'Show parts of a whole, with the total free to sit in the centre.',
  radar: 'Compare several categories at once around a central axis.',
  waterfall: 'Show how a starting value builds up or down to a final total.',
  packedBubble: 'Compare relative sizes as clustered bubbles.',
  funnel: 'Show how a quantity narrows through successive stages.',
  pyramid: 'Show proportions stacked from a broad base to a narrow top.',
  scatter: 'Plot one value against another to spot a relationship.',
  bubble: 'Plot two values against each other, sized by a third.',
  heatmap: 'Show intensity across two categorical dimensions in a grid.',
  sankey: 'Show how volume flows from one set of categories to another.',
};

// The option-builder family each chart type belongs to (mirrors the dispatch in
// buildChartOptions.ts). Types in the same family share a data shape, so they
// can be swapped without re-querying.
type ChartFamily =
  | 'cartesian'
  | 'slice'
  | 'polar'
  | 'waterfall'
  | 'xy'
  | 'crosstab';

const FAMILY: Record<InsightsChartType, ChartFamily> = {
  bar: 'cartesian',
  column: 'cartesian',
  line: 'cartesian',
  area: 'cartesian',
  pie: 'slice',
  donut: 'slice',
  funnel: 'slice',
  pyramid: 'slice',
  packedBubble: 'slice',
  radar: 'polar',
  waterfall: 'waterfall',
  scatter: 'xy',
  bubble: 'xy',
  heatmap: 'crosstab',
  sankey: 'crosstab',
};

// Which chart types render correctly for THIS spec's data. The inline builders
// only read `categories` + `series`, so compatibility is a function of the
// current type's family and the series count — not the source metrics. This
// keeps the selector from ever offering a type that would render blank or drop
// data (the same shape invariants canSaveSpec enforces for the save path).
//
// - xy (scatter/bubble): X=series[0], Y=series[1]; only swappable with each
//   other (a category chart would mis-plot the value axes).
// - crosstab (heatmap/sankey): a two-dimension matrix that also reads as a
//   grouped multi-series cartesian chart, so those are offered too.
// - the category families (cartesian/slice/polar/waterfall) all plot
//   `categories` × N series. Any of them can become bar/column/line/area/radar;
//   the single-series kinds (pie/donut/funnel/pyramid/packedBubble/waterfall,
//   which only plot series[0]) are added only when there's exactly one series.
export const getCompatibleChartTypes = (
  spec: InsightsChartSpec,
): InsightsChartType[] => {
  const family = FAMILY[spec.chartType];
  const seriesCount = spec.series.length;
  // Always keep the current type present, whatever the rules below decide.
  const allowed = new Set<InsightsChartType>([spec.chartType]);
  const add = (types: InsightsChartType[]) =>
    types.forEach((t) => allowed.add(t));

  if (family === 'xy') {
    add(['scatter', 'bubble']);
  } else if (family === 'crosstab') {
    add(['heatmap', 'sankey', 'bar', 'column', 'line', 'area']);
  } else {
    add(['bar', 'column', 'line', 'area', 'radar']);
    if (seriesCount === 1) {
      add(['pie', 'donut', 'funnel', 'pyramid', 'packedBubble', 'waterfall']);
    }
  }

  // Return in the canonical INSIGHTS_CHART_TYPES order for a stable menu.
  return INSIGHTS_CHART_TYPES.filter((t) => allowed.has(t));
};
